import React, { useState, useEffect, useCallback } from 'react';
import { Users, Trophy, MapPin, Flame, Heart, Sparkles, TrendingUp, MessageCircle, Plus, ThumbsUp, Calendar, Swords, Star, Crown, Zap, Music, BookOpen } from 'lucide-react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import DailySocialDareCard from '../components/dashboard/DailySocialDareCard';

const CATEGORY_COLORS = {
  social: 'bg-blue-100 text-blue-700',
  kindness: 'bg-pink-100 text-pink-700',
  listening: 'bg-green-100 text-green-700',
  confidence: 'bg-amber-100 text-amber-700',
  assertiveness: 'bg-purple-100 text-purple-700',
  gratitude: 'bg-teal-100 text-teal-700',
  empathy: 'bg-indigo-100 text-indigo-700',
  custom: 'bg-gray-100 text-gray-700',
};

const CommunityPage = () => {
  const navigate = useNavigate();
  const [feed, setFeed] = useState(null);
  const [score, setScore] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [spotlight, setSpotlight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  const [newTemplate, setNewTemplate] = useState({ title: '', description: '', category: 'social', duration_days: 7, goal_count: 3 });
  const [creating, setCreating] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [feedData, scoreData, templatesData, spotlightData] = await Promise.all([
        apiClient.request('/api/community/feed'),
        apiClient.request('/api/community/confidence-score'),
        apiClient.request('/api/community/challenge-templates'),
        apiClient.request('/api/self-dev/community-spotlight').catch(() => null),
      ]);
      setFeed(feedData);
      setScore(scoreData);
      setTemplates(templatesData.templates || []);
      if (spotlightData) setSpotlight(spotlightData);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreateTemplate = async () => {
    if (!newTemplate.title || !newTemplate.description) return;
    setCreating(true);
    try {
      await apiClient.request('/api/community/challenge-templates', {
        method: 'POST',
        body: JSON.stringify(newTemplate),
      });
      setShowCreateTemplate(false);
      setNewTemplate({ title: '', description: '', category: 'social', duration_days: 7, goal_count: 3 });
      fetchData();
    } catch {}
    setCreating(false);
  };

  const handleLike = async (templateId) => {
    try {
      await apiClient.request(`/api/community/challenge-templates/${templateId}/like`, { method: 'POST' });
      fetchData();
    } catch {}
  };

  if (loading) return <DashboardLayout><div className="p-8 text-center text-gray-400">Loading community...</div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-8 max-w-5xl mx-auto" data-testid="community-page">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-black text-gray-900 flex items-center justify-center gap-2">
            <Users size={32} className="text-indigo-600" />
            Community
          </h1>
          <p className="text-sm text-gray-500 mt-1">Practice together, grow together, meet for real</p>
        </div>

        {/* Stats Bar */}
        {feed?.stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8" data-testid="community-stats">
            <div className="bg-white rounded-xl p-4 border border-gray-100 text-center shadow-sm">
              <p className="text-2xl font-black text-indigo-600">{feed.stats.total_users}</p>
              <p className="text-[10px] text-gray-400 uppercase tracking-wider">Members</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-gray-100 text-center shadow-sm">
              <p className="text-2xl font-black text-green-600">{feed.stats.challenges_completed}</p>
              <p className="text-[10px] text-gray-400 uppercase tracking-wider">Challenges Done</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-gray-100 text-center shadow-sm">
              <p className="text-2xl font-black text-teal-600">{feed.stats.meetups_completed}</p>
              <p className="text-[10px] text-gray-400 uppercase tracking-wider">Meetups</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-gray-100 text-center shadow-sm">
              <p className="text-2xl font-black text-amber-600">{feed.stats.active_this_week}</p>
              <p className="text-[10px] text-gray-400 uppercase tracking-wider">Active This Week</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Feed */}
          <div className="lg:col-span-2 space-y-6">
            {/* Quick Actions */}
            <div className="flex flex-wrap gap-2">
              <button onClick={() => navigate('/friend-challenges')} className="flex items-center gap-1.5 px-4 py-2 bg-indigo-100 text-indigo-700 text-sm font-semibold rounded-xl hover:bg-indigo-200 transition-all" data-testid="go-challenges-btn">
                <Swords size={14} /> Challenges
              </button>
              <button onClick={() => navigate('/meetups')} className="flex items-center gap-1.5 px-4 py-2 bg-teal-100 text-teal-700 text-sm font-semibold rounded-xl hover:bg-teal-200 transition-all" data-testid="go-meetups-btn">
                <MapPin size={14} /> Meetups
              </button>
              <button onClick={() => navigate('/leaderboard')} className="flex items-center gap-1.5 px-4 py-2 bg-amber-100 text-amber-700 text-sm font-semibold rounded-xl hover:bg-amber-200 transition-all" data-testid="go-leaderboard-btn">
                <Trophy size={14} /> Leaderboard
              </button>
              <button onClick={() => navigate('/growth-story')} className="flex items-center gap-1.5 px-4 py-2 bg-purple-100 text-purple-700 text-sm font-semibold rounded-xl hover:bg-purple-200 transition-all" data-testid="go-growth-btn">
                <BookOpen size={14} /> My Story
              </button>
              <button onClick={() => navigate('/mood-playlist')} className="flex items-center gap-1.5 px-4 py-2 bg-violet-100 text-violet-700 text-sm font-semibold rounded-xl hover:bg-violet-200 transition-all" data-testid="go-playlist-btn">
                <Music size={14} /> Playlist
              </button>
            </div>

            {/* Recent Completions */}
            <div>
              <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Sparkles size={14} className="text-amber-500" /> Recent Wins
              </h2>
              {(feed?.recent_completions || []).length > 0 ? (
                <div className="space-y-2">
                  {feed.recent_completions.slice(0, 6).map((c, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-gray-100 shadow-sm">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${CATEGORY_COLORS[c.category] || 'bg-gray-100'}`}>
                        <Trophy size={14} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{c.title}</p>
                        <p className="text-[10px] text-gray-400">{c.participants}{c.is_group ? ' (group)' : ''}</p>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${CATEGORY_COLORS[c.category] || 'bg-gray-100'}`}>{c.category}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 p-4 bg-gray-50 rounded-xl">No completions this week yet. Be the first!</p>
              )}
            </div>

            {/* Recent Meetups */}
            {(feed?.recent_meetups || []).length > 0 && (
              <div>
                <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <MapPin size={14} className="text-teal-500" /> Recent Meetups
                </h2>
                <div className="space-y-2">
                  {feed.recent_meetups.map((m, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-teal-50 rounded-xl border border-teal-100">
                      <MapPin size={16} className="text-teal-600 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{m.title}</p>
                        <p className="text-[10px] text-gray-400">{m.participants} at {m.location_type}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Community Templates */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                  <MessageCircle size={14} className="text-violet-500" /> Community Challenges
                </h2>
                <button onClick={() => setShowCreateTemplate(!showCreateTemplate)} className="flex items-center gap-1 px-3 py-1.5 bg-violet-100 text-violet-700 text-xs font-semibold rounded-lg hover:bg-violet-200" data-testid="create-template-btn">
                  <Plus size={12} /> Create
                </button>
              </div>

              {showCreateTemplate && (
                <div className="mb-4 p-4 bg-violet-50 rounded-xl border border-violet-200" data-testid="create-template-panel">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                    <input type="text" value={newTemplate.title} onChange={e => setNewTemplate({...newTemplate, title: e.target.value})} placeholder="Challenge title" className="px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="template-title-input" />
                    <select value={newTemplate.category} onChange={e => setNewTemplate({...newTemplate, category: e.target.value})} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="template-category-select">
                      {Object.keys(CATEGORY_COLORS).map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <textarea value={newTemplate.description} onChange={e => setNewTemplate({...newTemplate, description: e.target.value})} placeholder="Describe the challenge..." className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-3" rows={2} data-testid="template-description-input" />
                  <div className="flex gap-3 mb-3">
                    <div className="flex-1">
                      <label className="text-[10px] text-gray-500">Duration (days)</label>
                      <input type="number" value={newTemplate.duration_days} onChange={e => setNewTemplate({...newTemplate, duration_days: parseInt(e.target.value) || 7})} className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm" min={1} max={30} />
                    </div>
                    <div className="flex-1">
                      <label className="text-[10px] text-gray-500">Goal count</label>
                      <input type="number" value={newTemplate.goal_count} onChange={e => setNewTemplate({...newTemplate, goal_count: parseInt(e.target.value) || 3})} className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm" min={1} max={20} />
                    </div>
                  </div>
                  <button onClick={handleCreateTemplate} disabled={creating} className="w-full py-2 bg-gradient-to-r from-violet-500 to-purple-600 text-white font-semibold rounded-lg text-sm disabled:opacity-50" data-testid="submit-template-btn">{creating ? 'Creating...' : 'Share with Community'}</button>
                </div>
              )}

              {templates.length > 0 ? (
                <div className="space-y-2">
                  {templates.slice(0, 8).map(t => (
                    <div key={t.template_id} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-gray-100 shadow-sm" data-testid={`community-template-${t.template_id}`}>
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${CATEGORY_COLORS[t.category] || 'bg-gray-100'}`}>
                        <Swords size={14} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{t.title}</p>
                        <p className="text-[10px] text-gray-400">by {t.creator_name} · {t.duration_days}d · {t.goal_count} goals</p>
                      </div>
                      <button onClick={() => handleLike(t.template_id)} className="flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-pink-50 text-pink-600 hover:bg-pink-100 transition-all" data-testid={`like-template-${t.template_id}`}>
                        <Heart size={12} fill={t.likes > 0 ? 'currentColor' : 'none'} />
                        <span className="font-bold">{t.likes || 0}</span>
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 p-4 bg-gray-50 rounded-xl">No community challenges yet. Be the first to create one!</p>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Social Confidence Score */}
            {score && (
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-testid="confidence-score-card">
                <div className="p-5 text-center" style={{ background: `linear-gradient(135deg, ${score.tier_color}15, ${score.tier_color}05)` }}>
                  <div className="w-20 h-20 rounded-full mx-auto mb-3 flex items-center justify-center border-4" style={{ borderColor: score.tier_color }}>
                    <span className="text-2xl font-black" style={{ color: score.tier_color }}>{score.score}</span>
                  </div>
                  <p className="text-lg font-black text-gray-900">{score.level}</p>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider">Social Confidence Score</p>
                </div>
                <div className="px-5 py-3">
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>Next: {score.next_milestone}</span>
                    <span>{score.progress_to_next}%</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-4">
                    <div className="h-full rounded-full transition-all" style={{ width: `${score.progress_to_next}%`, backgroundColor: score.tier_color }} />
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">Challenges</span>
                      <span className="font-bold text-gray-700">{score.breakdown.challenges} pts</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">Meetups</span>
                      <span className="font-bold text-gray-700">{score.breakdown.meetups} pts</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">Conversations</span>
                      <span className="font-bold text-gray-700">{score.breakdown.conversations} pts</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">Streaks</span>
                      <span className="font-bold text-gray-700">{score.breakdown.streaks} pts</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Stats */}
            {score?.stats && (
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
                <h3 className="text-sm font-bold text-gray-900 mb-3">Your Journey</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Swords size={14} className="text-indigo-500" />
                    <span className="text-xs text-gray-600 flex-1">{score.stats.challenges_completed} challenges completed</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin size={14} className="text-teal-500" />
                    <span className="text-xs text-gray-600 flex-1">{score.stats.meetups_completed} meetups attended</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MessageCircle size={14} className="text-blue-500" />
                    <span className="text-xs text-gray-600 flex-1">{score.stats.conversations} conversations</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Flame size={14} className="text-orange-500" />
                    <span className="text-xs text-gray-600 flex-1">{score.stats.daily_streak}-day streak</span>
                  </div>
                </div>
              </div>
            )}

            {/* Community Spotlight */}
            {spotlight?.spotlight && (
              <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-2xl border border-amber-200 shadow-sm overflow-hidden" data-testid="community-spotlight">
                <div className="p-5 text-center">
                  <Crown size={24} className="text-amber-500 mx-auto mb-2" />
                  <p className="text-[10px] uppercase tracking-wider font-bold text-amber-600 mb-1">Weekly Spotlight</p>
                  <p className="text-lg font-black text-gray-900">{spotlight.spotlight.name}</p>
                  <p className="text-xs text-gray-500 mt-1">Week of {spotlight.week}</p>
                  <div className="flex justify-center gap-3 mt-3 text-xs">
                    {spotlight.spotlight.challenges > 0 && (
                      <span className="flex items-center gap-1 text-indigo-600"><Swords size={10} /> {spotlight.spotlight.challenges}</span>
                    )}
                    {spotlight.spotlight.meetups > 0 && (
                      <span className="flex items-center gap-1 text-teal-600"><MapPin size={10} /> {spotlight.spotlight.meetups}</span>
                    )}
                    {spotlight.spotlight.dares > 0 && (
                      <span className="flex items-center gap-1 text-orange-600"><Zap size={10} /> {spotlight.spotlight.dares}</span>
                    )}
                  </div>
                </div>
                {spotlight.runners_up.length > 0 && (
                  <div className="px-5 py-2.5 bg-amber-100/50 border-t border-amber-200 flex justify-center gap-4 text-[10px] text-amber-700">
                    {spotlight.runners_up.map((r, i) => (
                      <span key={i} className="font-medium">{i === 0 ? '2nd' : '3rd'}: {r.name}</span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Daily Social Dare */}
            <DailySocialDareCard />
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CommunityPage;
